import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./Account2.css";
import { auth, db, functions } from "../Security/firebase-config";
import {
  linkWithCredential,
  fetchSignInMethodsForEmail,
  reauthenticateWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  deleteField,
  onSnapshot,
} from "firebase/firestore";

import NotificationPrefsPanel from "../components/Notifications/NotificationPrefsPanel";
import Footer from "../components/core-components/Footer";
import CustomAlert from "../components/core-components/CustomAlert";
import { httpsCallable } from "firebase/functions";
import { FUNCTION_NAMES } from "../utils/functionNames";
import Billing from "./Billing/Billing";

const Account2 = () => {
  const [userData, setUserData] = useState({
    username: "Loading...",
    email: "Loading...",
    createdAt: "Loading...",
    specialCode: "Loading...",
    discordId: null,
    discordUsername: null,
  });

  const location = useLocation();

  const [activeTab, setActiveTab] = useState("overview");
  const [openSection, setOpenSection] = useState(null);

  const [specialCode, setSpecialCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [linkEmail, setLinkEmail] = useState("");
  const [linkPassword, setLinkPassword] = useState("");

  const [alert, setAlert] = useState({ message: "", type: "" });
  const [showDeleteAccountMenu, setShowDeleteAccountMenu] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [lastUsernameChange, setLastUsernameChange] = useState(null);

  const [billingPromoStatus, setBillingPromoStatus] = useState([]);
  const [loadingBillingPromos, setLoadingBillingPromos] = useState(false);

  const [activeNotifScope, setActiveNotifScope] = useState("global");
  const [discordEnabled, setDiscordEnabled] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);

  const toggle = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  useLayoutEffect(() => {
    if (location.hash === "#subscription") {
      setTimeout(() => {
        const el = document.getElementById("subscription");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
    }
  }, [location.hash]);

  useEffect(() => {
    const normalizedHash = String(location.hash || "").trim().toLowerCase();

    if (normalizedHash === "#notifications") {
      setActiveTab("notifications");
      return;
    }

    if (normalizedHash === "#billing" || normalizedHash === "#subscription") {
      setActiveTab("billing");
      return;
    }

    if (normalizedHash === "#settings") {
      setActiveTab("settings");
      return;
    }

    if (!normalizedHash || normalizedHash === "#overview") {
      setActiveTab("overview");
    }
  }, [location.hash]);

  const showAlert = (message, type = "info") => {
    setAlert({ message, type });
    setTimeout(() => {
      setAlert({ message: "", type: "" });
    }, 5000);
  };

  const deleteAccountCallable = httpsCallable(functions, "deleteUserAccount");

  const formatTimestampValue = (value) => {
    if (!value) return "Not specified";

    const resolvedDate =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?.seconds === "number"
          ? new Date(value.seconds * 1000)
          : value instanceof Date
            ? value
            : null;

    if (!resolvedDate || Number.isNaN(resolvedDate.getTime())) {
      return "Not specified";
    }

    return resolvedDate.toLocaleString();
  };

  const loadBillingPromoStatus = useCallback(async () => {
    try {
      setLoadingBillingPromos(true);

      const getMyBillingPromoStatus = httpsCallable(
        functions,
        FUNCTION_NAMES.getMyBillingPromoStatus
      );

      const result = await getMyBillingPromoStatus({});

      setBillingPromoStatus(
        Array.isArray(result?.data?.promoStatus) ? result.data.promoStatus : []
      );
    } catch (error) {
      console.error("Error loading billing promo status:", error);
      setBillingPromoStatus([]);
    } finally {
      setLoadingBillingPromos(false);
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async (user) => {
      try {
        const userRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          setUserData({
            username: data.username || "Not set",
            email: data.email || "Not set",
            createdAt: data.createdAt
              ? data.createdAt.toDate().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              : "Unknown",
            specialCode: data.specialCode ? "Yes" : "No",
            discordId: data.discordId || null,
            discordUsername: data.discordUsername || null,
          });

          setLinkEmail(data.email || user.email || "");

          setLastUsernameChange(
            data.lastUsernameChangedAt
              ? data.lastUsernameChangedAt.toDate()
              : null
          );
        } else {
          showAlert("User data not found!", "error");
        }
      } catch (error) {
        console.error("Error fetching account details:", error);
        showAlert("An error occurred while fetching your account details.", "error");
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserData(user);
        loadBillingPromoStatus();
      } else {
        showAlert("No user is logged in!", "error");
        window.location.href = "/login";
      }
    });

    return () => unsubscribe();
  }, [loadBillingPromoStatus]);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const stop = onSnapshot(doc(db, "users", user.uid), async (snap) => {
        const data = snap.data() || {};

        setDiscordEnabled(!!data.discordNotificationsEnabled);
        setActiveNotifScope(data.activeTeamId || "global");

        const ids = Array.isArray(data.teamIds) ? data.teamIds : [];
        if (!ids.length) {
          setTeamOptions([]);
          return;
        }

        const docs = await Promise.all(
          ids.map((id) => getDoc(doc(db, "teams", id)))
        );

        const options = docs
          .map((teamSnap, index) =>
            teamSnap.exists()
              ? { id: ids[index], name: teamSnap.data().teamName || ids[index] }
              : null
          )
          .filter(Boolean);

        setTeamOptions(options);
      });

      return () => stop();
    });

    return () => unsubAuth();
  }, []);

  const handleSpecialCodeSubmit = async (e) => {
    e.preventDefault();

    const code = specialCode.trim();

    if (!code) {
      showAlert("Please enter a promo code.", "error");
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        showAlert("No user is logged in!", "error");
        return;
      }

      const applyPromo = httpsCallable(
        functions,
        FUNCTION_NAMES.claimBillingPromoCode
      );

      const res = await applyPromo({ code });

      setBillingPromoStatus(
        Array.isArray(res?.data?.promoStatus) ? res.data.promoStatus : []
      );

      showAlert("Billing promo code claimed successfully.", "success");
      setSpecialCode("");
    } catch (error) {
      console.error("Error applying promo code:", error);

      const msg =
        error?.message?.includes("Invalid promo code") ||
          error?.message?.includes("permission-denied")
          ? error?.message || "Invalid promo code. Please try again."
          : error?.message || "Failed to apply promo code.";

      showAlert(msg, "error");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/";
    } catch (error) {
      console.error("Error logging out:", error);
      showAlert("Failed to log out. Please try again.", "error");
    }
  };

  const handleConfirmDeleteAccount = async () => {
    try {
      const user = auth.currentUser;

      if (!user) {
        showAlert("No user logged in.", "error");
        return;
      }

      setIsDeletingAccount(true);

      await deleteAccountCallable();

      setShowDeleteAccountMenu(false);
      showAlert("Your account has been deleted.", "success");

      try {
        await signOut(auth);
      } catch (e) {
        console.error(e);
      }

      window.location.href = "/";
    } catch (error) {
      console.error("Delete account failed:", error);
      showAlert("Failed to delete account. Please try again.", "error");
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      showAlert("New passwords do not match!", "error");
      return;
    }

    if (newPassword.length < 6) {
      showAlert("Password should be at least 6 characters long.", "error");
      return;
    }

    try {
      const user = auth.currentUser;

      if (!user) {
        showAlert("No user logged in.", "error");
        return;
      }

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      showAlert("Password changed successfully!", "success");

      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      console.error("Error changing password:", error);
      showAlert(
        "Failed to change password. Check your current password and try again.",
        "error"
      );
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();

    if (
      lastUsernameChange &&
      Date.now() - lastUsernameChange.getTime() < 30 * 24 * 60 * 60 * 1000
    ) {
      const msLeft =
        30 * 24 * 60 * 60 * 1000 - (Date.now() - lastUsernameChange.getTime());
      const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

      showAlert(
        `You can change your username again in ${daysLeft} day(s).`,
        "error"
      );
      return;
    }

    const raw = (newUsername || "").trim();

    if (!raw) {
      showAlert("Username cannot be empty.", "error");
      return;
    }

    try {
      const user = auth.currentUser;

      if (!user) {
        showAlert("No user logged in.", "error");
        return;
      }

      const fn = httpsCallable(functions, "changeUsername");
      const res = await fn({ newUsername: raw });
      const updated = res?.data?.username || raw;

      setUserData((prev) => ({
        ...prev,
        username: updated,
      }));

      setLastUsernameChange(new Date());
      setNewUsername("");

      showAlert("Username changed successfully!", "success");
    } catch (err) {
      console.error(err);

      const msg = err?.message || "";

      if (msg.includes("USERNAME_TAKEN")) {
        showAlert("That username is already taken. Try another one.", "error");
        return;
      }

      if (msg.includes("You can change your username again")) {
        showAlert(msg.replace("functions/", ""), "error");
        return;
      }

      if (msg.includes("Username must be")) {
        showAlert(msg.replace("functions/", ""), "error");
        return;
      }

      showAlert("Failed to change username.", "error");
    }
  };

  const handleConnectDiscord = async () => {
    const user = auth.currentUser;

    if (!user) {
      showAlert("You must be logged in to connect Discord", "error");
      return;
    }

    const url = new URL("https://discord.com/oauth2/authorize");
    url.searchParams.set("client_id", process.env.REACT_APP_DISCORD_CLIENT_ID);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("redirect_uri", process.env.REACT_APP_DISCORD_REDIRECT_URI);
    url.searchParams.set("scope", "identify email guilds.join");
    url.searchParams.set("state", await user.getIdToken(true));

    window.location.href = url.toString();
  };

  const handleDisconnectDiscord = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const userRef = doc(db, "users", user.uid);

      await updateDoc(userRef, {
        discordId: deleteField(),
        discordUsername: deleteField(),
      });

      setUserData((prev) => ({
        ...prev,
        discordId: undefined,
        discordUsername: undefined,
      }));

      showAlert("Discord disconnected", "success");
    } catch (error) {
      console.error(error);
      showAlert("Could not disconnect Discord", "error");
    }
  };

  const handleLinkEmailPassword = async (e) => {
    e.preventDefault();

    try {
      const user = auth.currentUser;

      if (!user) {
        showAlert("You must be logged in.", "error");
        return;
      }

      if (!linkEmail || !linkPassword) {
        showAlert("Please enter email and password.", "error");
        return;
      }

      const methods = await fetchSignInMethodsForEmail(auth, linkEmail);

      if (methods.includes("password")) {
        showAlert(
          "Email & password login is already enabled for this email.",
          "info"
        );
        return;
      }

      if (user.email !== linkEmail) {
        try {
          await user.updateEmail(linkEmail);
        } catch (err) {
          if (err.code === "auth/requires-recent-login") {
            try {
              const provider = new GoogleAuthProvider();
              await reauthenticateWithPopup(user, provider);
              await user.updateEmail(linkEmail);
            } catch (reauthErr) {
              console.error(reauthErr);
              showAlert("Please re-login and try again.", "error");
              return;
            }
          } else {
            console.error(err);
            showAlert("Could not set email on your account.", "error");
            return;
          }
        }
      }

      const cred = EmailAuthProvider.credential(linkEmail, linkPassword);
      await linkWithCredential(user, cred);

      showAlert("Email & password login enabled for your account!", "success");
      setLinkPassword("");
    } catch (err) {
      console.error("link email/password error:", err);

      if (
        err.code === "auth/credential-already-in-use" ||
        err.code === "auth/email-already-in-use"
      ) {
        showAlert(
          "That email is already used by a different account. Sign in with that account and link your providers there.",
          "error"
        );
        return;
      }

      showAlert("Could not enable email & password login.", "error");
    }
  };

  const renderDiscordBlock = () => {
    return (
      <div className="--Account2__discordBlock">
        <div className="--Account2__discordHeader">Discord connected:</div>

        {userData.discordId ? (
          <div className="--Account2__discordConnectedState">
            <div className="--Account2__discordName">
              {userData.discordUsername}
            </div>
            <button
              type="button"
              className="--Account2__discordDisconnectButton"
              onClick={handleDisconnectDiscord}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="--Account2__discordDisconnectedState">
            <div className="--Account2__discordEmptyText">
              No Discord account connected
            </div>
            <button
              type="button"
              className="--Account2__discordConnectButton"
              onClick={handleConnectDiscord}
            >
              Connect Discord
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderOverviewTab = () => {
    return (
      <div className="--Account2__tabContent">
        <div className="--Account2__sectionHeading">Your Account</div>

        <div className="--Account2__overviewList">
          <div className="--Account2__detailRow">
            <span className="--Account2__detailLabel">Username:</span>
            <span className="--Account2__detailValue">{userData.username}</span>
          </div>

          <div className="--Account2__detailRow">
            <span className="--Account2__detailLabel">Email:</span>
            <span className="--Account2__detailValue">{userData.email}</span>
          </div>

          <div className="--Account2__detailRow">
            <span className="--Account2__detailLabel">Account Created:</span>
            <span className="--Account2__detailValue">{userData.createdAt}</span>
          </div>

          <div className="--Account2__detailRow">
            <span className="--Account2__detailLabel">
              Legacy Access Code Used:
            </span>
            <span className="--Account2__detailValue">{userData.specialCode}</span>
          </div>
        </div>

        {renderDiscordBlock()}
      </div>
    );
  };

  const renderBillingTab = () => {
    return <Billing embedded />;
  };

  const renderSettingsTab = () => {
    return (
      <div className="--Account2__tabContent">
        <div className="--Account2__sectionHeading">Settings</div>

        <div className="--Account2__buttonStack">
          <button
            type="button"
            className="--Account2__actionToggle"
            onClick={() => toggle("username")}
          >
            Change Username
          </button>

          {openSection === "username" && (
            <form
              className="--Account2__formBlock"
              onSubmit={handleChangeUsername}
            >
              <input
                type="text"
                className="--Account2__input"
                placeholder="New Username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
              />
              <button type="submit" className="--Account2__submitButton">
                Update Username
              </button>
            </form>
          )}

          <button
            type="button"
            className="--Account2__actionToggle"
            onClick={() => toggle("password")}
          >
            Change Password
          </button>

          {openSection === "password" && (
            <form
              className="--Account2__formBlock"
              onSubmit={handleChangePassword}
            >
              <input
                type="password"
                className="--Account2__input"
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <input
                type="password"
                className="--Account2__input"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <input
                type="password"
                className="--Account2__input"
                placeholder="Confirm New Password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
              <button type="submit" className="--Account2__submitButton">
                Update Password
              </button>
            </form>
          )}

          <button
            type="button"
            className="--Account2__actionToggle"
            onClick={() => toggle("link")}
          >
            Add Email & Password Login
          </button>

          {openSection === "link" && (
            <form
              className="--Account2__formBlock"
              onSubmit={handleLinkEmailPassword}
            >
              <input
                type="email"
                className="--Account2__input"
                placeholder="Email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                required
              />
              <input
                type="password"
                className="--Account2__input"
                placeholder="New Password"
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                required
              />
              <button type="submit" className="--Account2__submitButton">
                Enable Email + Password
              </button>
            </form>
          )}

          <button
            type="button"
            className="--Account2__actionToggle"
            onClick={() => toggle("code")}
          >
            Billing Promo Code
          </button>

          {openSection === "code" && (
            <div className="--Account2__formBlock">
              <form onSubmit={handleSpecialCodeSubmit}>
                <input
                  type="text"
                  className="--Account2__input"
                  placeholder="Enter Code"
                  value={specialCode}
                  onChange={(e) => setSpecialCode(e.target.value)}
                  required
                />
                <button type="submit" className="--Account2__submitButton">
                  Submit Code
                </button>
              </form>

              <div className="--Account2__promoSection">
                <div className="--Account2__promoHeading">
                  Current billing promos
                </div>

                {loadingBillingPromos ? (
                  <p className="--Account2__promoText">Loading promo status...</p>
                ) : billingPromoStatus.length === 0 ? (
                  <p className="--Account2__promoText">
                    No billing promo codes are currently attached to your account.
                  </p>
                ) : (
                  <div className="--Account2__promoList">
                    {billingPromoStatus.map((promo) => (
                      <div
                        key={promo.claimId}
                        className="--Account2__promoItem"
                      >
                        <div className="--Account2__promoLine">
                          <strong>{promo.displayName || promo.code}</strong>{" "}
                          <span>({promo.status || "claimed"})</span>
                        </div>

                        <div className="--Account2__promoLine">
                          <strong>Target:</strong>{" "}
                          <span>
                            {promo.targetType === "team_bill"
                              ? `Team bill${promo.attachedTeamName ? ` for ${promo.attachedTeamName}` : ""}`
                              : promo.attachedTeamName
                                ? `Your bill on ${promo.attachedTeamName}`
                                : "Your eligible billed share"}
                          </span>
                        </div>

                        <div className="--Account2__promoLine">
                          <strong>Discount:</strong>{" "}
                          <span>
                            {promo.discountType === "percent"
                              ? `${promo.discountPercent || 0}% off`
                              : Object.entries(
                                promo.fixedAmountMinorByCurrency || {}
                              )
                                .map(([currency, amountMinor]) => {
                                  if (typeof amountMinor !== "number") {
                                    return null;
                                  }

                                  return new Intl.NumberFormat(undefined, {
                                    style: "currency",
                                    currency,
                                    currencyDisplay: "code",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(amountMinor / 100);
                                })
                                .filter(Boolean)
                                .join(" / ")}
                          </span>
                        </div>

                        {promo.summary ? (
                          <div className="--Account2__promoLine">
                            <strong>Includes:</strong> <span>{promo.summary}</span>
                          </div>
                        ) : null}

                        <div className="--Account2__promoLine">
                          <strong>Duration:</strong>{" "}
                          <span>
                            {promo.durationMode === "forever"
                              ? "No cycle limit"
                              : typeof promo.remainingCycles === "number"
                                ? `${promo.remainingCycles} billing cycle${promo.remainingCycles === 1 ? "" : "s"} remaining`
                                : promo.durationMode || "Not specified"}
                          </span>
                        </div>

                        {promo.activeUntil ? (
                          <div className="--Account2__promoLine">
                            <strong>Active until:</strong>{" "}
                            <span>{formatTimestampValue(promo.activeUntil)}</span>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            className="--Account2__deleteButton"
            onClick={() => setShowDeleteAccountMenu(true)}
          >
            Delete Valovate Account
          </button>
        </div>
      </div>
    );
  };

  const renderNotificationTab = () => {
    return (
      <div className="--Account2__tabContent">
        <div className="--Account2__sectionHeading">Notification Settings</div>

        <div className="--Account2__notificationsLayout">
          <div className="--Account2__notificationsMain">
            <div className="--Account2__notificationsPanel">
              <NotificationPrefsPanel
                teamOptions={teamOptions}
                scope={activeNotifScope}
                discordId={userData.discordId}
                discordEnabled={discordEnabled}
                onNeedConnectDiscord={() => {
                  showAlert(
                    "Please connect Discord to enable DM notifications.",
                    "info"
                  );
                }}
              />
            </div>
          </div>

          <div className="--Account2__notificationsSidebar">
            {renderDiscordBlock()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="--Account2__page">
      {alert.message && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          position="top"
          onClose={() => setAlert({ message: "", type: "" })}
        />
      )}

      <div className="--Account2__section">
        <div className="--Account2__shell">
          <div className="--Account2__topbar">
            <button
              type="button"
              className={`--Account2__tabButton ${activeTab === "overview" ? "--Account2__tabButton--active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              Account overview
            </button>

            <button
              type="button"
              className={`--Account2__tabButton ${activeTab === "billing" ? "--Account2__tabButton--active" : ""}`}
              onClick={() => setActiveTab("billing")}
            >
              Billing
            </button>

            <button
              type="button"
              className={`--Account2__tabButton ${activeTab === "settings" ? "--Account2__tabButton--active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </button>

            <button
              type="button"
              className={`--Account2__tabButton ${activeTab === "notifications" ? "--Account2__tabButton--active" : ""}`}
              onClick={() => setActiveTab("notifications")}
            >
              Notification Settings
            </button>
          </div>

          <div className="--Account2__divider" />

          <div className="--Account2__content">
            {activeTab === "overview" && renderOverviewTab()}
            {activeTab === "billing" && renderBillingTab()}
            {activeTab === "settings" && renderSettingsTab()}
            {activeTab === "notifications" && renderNotificationTab()}
          </div>

          <div className="--Account2__footerActions">
            <button
              type="button"
              className="--Account2__logoutButton"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {showDeleteAccountMenu && (
        <div
          className="--Account2__deleteOverlay"
          onClick={() => {
            if (!isDeletingAccount) {
              setShowDeleteAccountMenu(false);
            }
          }}
        >
          <div
            className="--Account2__deleteModal"
            role="dialog"
            aria-modal="true"
            aria-label="Delete account confirmation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="--Account2__deleteModalHeader">
              <h2 className="--Account2__deleteModalTitle">Delete Account</h2>

              <button
                type="button"
                className="--Account2__deleteModalClose app-cancel-control"
                onClick={() => {
                  if (!isDeletingAccount) {
                    setShowDeleteAccountMenu(false);
                  }
                }}
                aria-label="Close delete menu"
              >
                ×
              </button>
            </div>

            <p className="--Account2__deleteModalText">
              <strong>This action is irreversible.</strong> Once you confirm,
              your account and associated data will be deleted.
            </p>

            <p className="--Account2__deleteModalText">
              For security, fraud prevention, and free-trial abuse protection,
              we retain a limited deletion record after account deletion.
            </p>

            <ul className="--Account2__deleteModalList">
              <li>Phone-verification HMAC, if one exists</li>
              <li>Deletion request timestamp</li>
              <li>Most recent recorded trial-use timestamp, if one exists</li>
              <li>
                Automatic expiry timestamp for deletion of that short-term
                abuse-monitor record
              </li>
              <li>Deletion timestamp</li>
            </ul>

            <p className="--Account2__deleteModalText">
              If you are currently a payer on any team, deletion also forces
              that team's subscription to end at the close of the current
              billing period and forces the remaining team to reapprove payer
              setup for the next cycle.
            </p>

            <p className="--Account2__deleteModalText">
              We also keep <strong>anonymous, non-identifiable</strong> deletion
              statistics permanently, such as aggregate deletion counts by date
              and hour. This data cannot be traced back to you and is used only
              to detect abuse patterns.
            </p>

            <div className="--Account2__deleteModalActions">
              <button
                type="button"
                className="--Account2__deleteCancelButton app-cancel-control"
                onClick={() => setShowDeleteAccountMenu(false)}
                disabled={isDeletingAccount}
              >
                Cancel
              </button>

              <button
                type="button"
                className="--Account2__deleteConfirmButton"
                onClick={handleConfirmDeleteAccount}
                disabled={isDeletingAccount}
              >
                {isDeletingAccount ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="footer-section">
        <Footer />
      </section>
    </div>
  );
};

export default Account2;
